/**
 * navStructure — the single source of truth for the top-level IA.
 *
 * The operator-approved 10-tab structure lives HERE as pure data:
 * TopMenu renders it, and navStructure.test.ts asserts every entry
 * (tab or dropdown item) resolves to a real routed ViewKey — so a nav
 * entry can never dead-click.
 *
 * Target IA (one coherent pass):
 *   1. Home            (Dashboard)
 *   2. CRM             (Accounts, People, Prospects, Customer Success)
 *   3. Projects        (Customer Pipeline Board, Tasks, Goals)
 *   4. Content         (Content Engine, Personas, Blog/SEO, Brand Brain)
 *   5. Email           (Email Marketing / sequences)
 *   6. Docs & Deals    (Data Room, Contracts, Proposals)
 *   7. Dev             (Development, Tickets, Hyper-Coder, Scoring Engine, Agents)
 *   8. Reports         (placeholder — labeled "coming soon", still a real view)
 *   9. Chat
 *  10. Admin           (Users, Settings, Audit/Ledger)
 *
 * Every route maps onto an EXISTING view component (plus the small
 * ReportsView placeholder) — no view was rebuilt, only regrouped.
 */
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  Building2,
  UserCircle2,
  Target,
  HeartPulse,
  FolderKanban,
  ListChecks,
  Sparkles,
  ScanFace,
  PenLine,
  BrainCircuit,
  Mail,
  Folder,
  Database,
  FileSignature,
  FileText,
  Code2,
  Ticket,
  Gauge,
  Bot,
  BarChart3,
  MessageSquare,
  Shield,
  Settings,
  History,
  Compass,
} from 'lucide-react';
import type { ViewKey } from '../components/TopMenu';
import type { ContactsTab } from '../views/PeopleView';
import type { ProjectsTab } from '../views/ProjectsView';
import type { AdminTab } from '../views/AdminView';
import type { ContentItemType } from './db/types';

/** Where a nav entry lands: a real routed view, optionally deep-linking
 *  a sub-tab inside that view. */
export interface NavRoute {
  view: ViewKey;
  /** Pre-select a sub-tab inside PeopleView (the CRM). */
  contactsTab?: ContactsTab;
  /** Pre-select a sub-tab inside ProjectsView. */
  projectsTab?: ProjectsTab;
  /** Pre-select a sub-tab inside AdminView. */
  adminTab?: AdminTab;
  /** Open ContentEngineView with this per-type workspace ALREADY open,
   *  skipping the generic type picker (e.g. Blog/SEO lands in the blog
   *  workspace with the SEO tooling in view). */
  contentType?: ContentItemType;
}

export interface NavDropdownEntry {
  id: string;
  title: string;
  sub: string;
  meta: string;
  icon: LucideIcon;
  route: NavRoute;
  /** Hide the entry from users below this role (e.g. Brand Brain lives
   *  in AdminView, which non-admins cannot open). */
  requiresRole?: 'admin' | 'staff';
}

export interface NavTab {
  /** Doubles as the view the tab itself opens when clicked. */
  id: ViewKey;
  label: string;
  icon: LucideIcon;
  items?: NavDropdownEntry[];
  requiresRole?: 'admin' | 'staff';
}

export const NAV_TABS: NavTab[] = [
  // 1 ─ Home ────────────────────────────────────────────────────────────
  { id: 'dashboard', label: 'Home', icon: LayoutDashboard },

  // 2 ─ CRM (moved out of the old "Contacts" grouping) ─────────────────
  {
    id: 'contacts',
    label: 'CRM',
    icon: Users,
    items: [
      {
        id: 'accounts',
        title: 'Accounts',
        sub: 'CRM list — POC, owner, stage rollup, last update.',
        meta: 'A',
        icon: Building2,
        route: { view: 'contacts', contactsTab: 'accounts' },
      },
      {
        id: 'people',
        title: 'People',
        sub: 'Contacts at active customer accounts.',
        meta: 'P',
        icon: UserCircle2,
        route: { view: 'contacts', contactsTab: 'contacts' },
      },
      {
        id: 'prospects',
        title: 'Prospects',
        sub: 'Pipeline of individuals we are pursuing.',
        meta: 'R',
        icon: Target,
        route: { view: 'contacts', contactsTab: 'prospects' },
      },
      {
        id: 'customer-success',
        title: 'Customer Success',
        sub: 'Live-customer health, ranked riskiest first.',
        meta: 'H',
        icon: HeartPulse,
        route: { view: 'customer-success' },
      },
    ],
  },

  // 3 ─ Projects (Workflows→"Customer Pipeline Board", Tasks, Goals) ───
  {
    id: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    items: [
      {
        id: 'clients',
        title: 'Clients',
        sub: 'Your client directory — add, search, and open a client.',
        meta: 'C',
        icon: Building2,
        route: { view: 'projects', projectsTab: 'clients' },
      },
      {
        id: 'projects',
        title: 'Projects',
        sub: 'Every project — filter, sort, list / kanban, drill in.',
        meta: 'P',
        icon: FolderKanban,
        route: { view: 'projects', projectsTab: 'projects' },
      },
      {
        id: 'tasks',
        title: 'Tasks',
        sub: 'Project to-dos — owners, due dates, checklists, recurring.',
        meta: 'T',
        icon: ListChecks,
        route: { view: 'projects', projectsTab: 'tasks' },
      },
      {
        id: 'goals',
        title: 'Goals',
        sub: 'Company goals, key results, and progress.',
        meta: 'G',
        icon: Target,
        route: { view: 'goals' },
      },
    ],
  },

  // 4 ─ Content (moved out of the old "Tools" grouping) ────────────────
  {
    id: 'content-engine',
    label: 'Content',
    icon: Sparkles,
    items: [
      {
        id: 'content-engine',
        title: 'Content Engine',
        sub: 'Generate blogs, proposals, comms, and decks on-brand.',
        meta: 'C',
        icon: Sparkles,
        route: { view: 'content-engine' },
      },
      {
        id: 'personas',
        title: 'Personas',
        sub: 'Build structured ICP personas from your CRM.',
        meta: 'P',
        icon: ScanFace,
        route: { view: 'contacts', contactsTab: 'persona' },
      },
      {
        id: 'blog-seo',
        title: 'Blog / SEO',
        sub: 'Score-as-you-type SEO editor inside the Content Engine.',
        meta: 'B',
        icon: PenLine,
        // Deep-links straight into the blog workspace (SEO/AEO panel in
        // view) rather than dumping into the generic Content picker.
        route: { view: 'content-engine', contentType: 'blog' },
      },
      {
        id: 'brand-brain',
        title: 'Brand Brain',
        sub: 'Voice, positioning, offers — keeps AI output on-brand.',
        meta: 'V',
        icon: BrainCircuit,
        route: { view: 'admin', adminTab: 'brain' },
        requiresRole: 'admin',
      },
    ],
  },

  // 5 ─ Email ──────────────────────────────────────────────────────────
  { id: 'email-marketing', label: 'Email', icon: Mail },

  // 6 ─ Docs & Deals (renamed "Files"; Customers moved into CRM) ───────
  {
    id: 'files',
    label: 'Docs & Deals',
    icon: Folder,
    items: [
      {
        id: 'data-room',
        title: 'Data Room',
        sub: 'Secure files for due diligence and counterparties.',
        meta: 'D',
        icon: Database,
        route: { view: 'files' },
      },
      {
        id: 'contracts',
        title: 'Contracts',
        sub: 'Signed and pending agreements, by status.',
        meta: 'C',
        icon: FileSignature,
        route: { view: 'contracts' },
      },
      {
        id: 'proposals',
        title: 'Proposals',
        sub: 'Org-wide proposal roll-up alongside contracts.',
        meta: 'P',
        icon: FileText,
        route: { view: 'contracts' },
      },
    ],
  },

  // 7 ─ Dev (Development, Tickets, Hyper-Coder, Scoring Engine, Agents) ─
  {
    id: 'tickets',
    label: 'Dev',
    icon: Code2,
    items: [
      {
        id: 'development',
        title: 'Development',
        sub: 'North star, roadmap, milestones, and agent workers.',
        meta: 'D',
        icon: Code2,
        route: { view: 'projects', projectsTab: 'development' },
      },
      {
        id: 'tickets',
        title: 'Tickets',
        sub: 'Bug and feature tickets — triage, assign, resolve.',
        meta: 'T',
        icon: Ticket,
        route: { view: 'tickets' },
      },
      {
        id: 'hyper-coder',
        title: 'HyperCoder',
        sub: 'Governed coding-agent dispatch — scoped contracts, reviewable patches, PR tracking.',
        meta: 'H',
        icon: Code2,
        route: { view: 'hyper-coder' },
      },
      {
        id: 'scoring-engine',
        title: 'Scoring Engine',
        sub: 'How lead, health, and AQI scoring works — with live scores.',
        meta: 'Q',
        icon: Gauge,
        route: { view: 'scoring-engine' },
      },
      {
        id: 'agents',
        title: 'Agents',
        sub: 'Deploy, schedule, and monitor autonomous agents.',
        meta: 'A',
        icon: Bot,
        route: { view: 'projects', projectsTab: 'agents' },
      },
    ],
  },

  // 8 ─ Reports (placeholder view — real render, never a dead click) ───
  { id: 'reports', label: 'Reports', icon: BarChart3 },

  // 9 ─ Chat ───────────────────────────────────────────────────────────
  { id: 'chat', label: 'Chat', icon: MessageSquare },

  // 10 ─ Admin ─────────────────────────────────────────────────────────
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    requiresRole: 'admin',
    items: [
      {
        id: 'users',
        title: 'Users',
        sub: 'Invite, change roles, disable accounts.',
        meta: 'U',
        icon: Users,
        route: { view: 'admin', adminTab: 'users' },
      },
      {
        id: 'setup',
        title: 'Setup',
        sub: 'Guided company brain, org KPIs, governance.',
        meta: 'G',
        icon: Compass,
        route: { view: 'admin', adminTab: 'setup' },
      },
      {
        id: 'settings',
        title: 'Settings',
        sub: 'Runtime + integration status.',
        meta: 'S',
        icon: Settings,
        route: { view: 'admin', adminTab: 'settings' },
      },
      {
        id: 'audit',
        title: 'Audit log',
        sub: 'Agent activity & approvals ledger.',
        meta: 'A',
        icon: History,
        route: { view: 'admin', adminTab: 'audit' },
      },
    ],
  },
];
